// src/pages/business/commission/CommissionPayout.tsx
// Commission payout management page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, Wallet, Loader2, Search, Filter,
  User, Calendar, CheckCircle, Clock, ChevronDown,
  Download, TrendingUp, AlertCircle
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface CommissionPayout {
  id: string;
  recipient_name: string;
  recipient_phone: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payment_method: 'mpesa' | 'bank' | 'cash';
  reference?: string;
  notes?: string;
  created_at: string;
  processed_at?: string;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)' },
  processing: { label: 'Processing', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)' },
  completed: { label: 'Completed', color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' },
  failed: { label: 'Failed', color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' },
};

const paymentMethodConfig: Record<string, { label: string; icon: React.ElementType }> = {
  mpesa: { label: 'M-Pesa', icon: CreditCard },
  bank: { label: 'Bank Transfer', icon: Wallet },
  cash: { label: 'Cash', icon: CreditCard },
};

const CommissionPayout: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [payouts, setPayouts] = useState<CommissionPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | CommissionPayout['status']>('all');

  useEffect(() => {
    fetchPayouts();
  }, [businessId]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/commission-payouts`);
      setPayouts(res.data.payouts || []);
    } catch (error: any) {
      console.error('Failed to fetch payouts:', error);
      toastError('Failed to load commission payouts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchPayouts();
  };

  const processPayout = async (payoutId: string) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/commission-payouts/${payoutId}/process`);
      toastSuccess('Payout processed successfully');
      fetchPayouts();
    } catch (error: any) {
      toastError('Failed to process payout');
    }
  };

  const filteredPayouts = payouts.filter((p) => {
    const matchesFilter = filter === 'all' || p.status === filter;
    const matchesSearch = searchQuery === '' || 
      p.recipient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.recipient_phone.includes(searchQuery);
    return matchesFilter && matchesSearch;
  });

  const stats = {
    pending: payouts.filter(p => p.status === 'pending').length,
    completed: payouts.filter(p => p.status === 'completed').length,
    total: payouts.length,
    totalPending: payouts
      .filter(p => p.status === 'pending' || p.status === 'processing')
      .reduce((sum, p) => sum + p.amount, 0),
    totalPaid: payouts
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Commission Payouts
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Commission Payouts
        </h1>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{stats.total}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{stats.pending}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Pending</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
              KES {stats.totalPaid.toLocaleString()}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Paid</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
              KES {stats.totalPending.toLocaleString()}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Pending Amount</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setFilter(filter === 'all' ? 'pending' : filter === 'pending' ? 'processing' : filter === 'processing' ? 'completed' : filter === 'completed' ? 'failed' : 'all')}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm font-semibold text-light-text dark:text-dark-text"
            >
              <Filter size={16} />
              {filter === 'all' ? 'All' : statusConfig[filter]?.label}
              <ChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Payouts List */}
        {filteredPayouts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Wallet size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Payouts Found
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery || filter !== 'all'
                ? 'Try adjusting your search or filter'
                : 'Commission payouts will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPayouts.map((payout) => {
              const status = statusConfig[payout.status];
              const PaymentIcon = paymentMethodConfig[payout.payment_method]?.icon || CreditCard;

              return (
                <div
                  key={payout.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                        <User size={20} style={{ color: '#4F46E5' }} />
                      </div>
                      <div>
                        <p className="font-bold text-light-text dark:text-dark-text">{payout.recipient_name}</p>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">{payout.recipient_phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg" style={{ color: '#10B981' }}>
                        KES {payout.amount.toLocaleString()}
                      </p>
                      <span
                        className="px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ backgroundColor: status.bgColor, color: status.color }}
                      >
                        {status.label}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                        <PaymentIcon size={16} style={{ color: '#4F46E5' }} />
                      </div>
                      <div>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Payment Method</p>
                        <p className="font-medium text-sm text-light-text dark:text-dark-text">
                          {paymentMethodConfig[payout.payment_method]?.label || payout.payment_method}
                        </p>
                      </div>
                    </div>
                    {payout.reference && (
                      <div>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Reference</p>
                        <p className="font-medium text-sm text-light-text dark:text-dark-text font-mono">
                          {payout.reference}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-light-subtle dark:text-dark-subtle">Created</p>
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">
                        {formatDate(payout.created_at)}
                      </p>
                    </div>
                  </div>

                  {payout.notes && (
                    <div className="mb-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                      <p className="text-sm text-light-subtle dark:text-dark-subtle">{payout.notes}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-light-border dark:border-dark-border">
                    <p className="text-xs text-light-subtle dark:text-dark-subtle flex items-center gap-1">
                      <Calendar size={12} />
                      {payout.processed_at ? `Processed: ${formatDate(payout.processed_at)}` : 'Not yet processed'}
                    </p>
                    {payout.status === 'pending' && (
                      <button
                        onClick={() => processPayout(payout.id)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                        style={{ backgroundColor: '#10B981' }}
                      >
                        <CheckCircle size={16} />
                        Process Payout
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <Loader2 size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default CommissionPayout;